import { useParams } from 'react-router-dom'
import {
  DonationCard,
  InnerSidebar,
  ProgressBar,
  SectionHeading,
  useLocale,
  useSeo,
  useText,
} from '@kkf/design'
import { useAppeals } from '../api/useAppeals'
import { GiveForm } from '../components/GiveForm'
import { INNER_SIDEBAR } from '../data/inner-sidebar'
import { formatMoney } from '../data/donations'
import { ROUTES, SITE } from '../data/seo'
import { useT } from '../i18n'

/**
 * The donation-details page — resolved from `/donations/:slug`, against the API's appeals.
 *
 * ## What changed on 2026-09-25
 *
 * The appeal comes from `GET /v1/donations/appeals` rather than from `data/donations.ts`, and
 * the form is `GiveForm` — the one the directory's panel uses. The template's form went with
 * it: a static "$10.00" that did not follow the picker, a "Donation Total: $10" that did not
 * either, a three-way payment-method radio PayPal's own window will ask, and a "Test mode is
 * enabled" notice for a test mode that did not exist. `GiveForm` says what is true instead:
 * nothing is charged on this site yet.
 *
 * ## An unknown slug still shows the first appeal
 *
 * Unchanged, and pinned in `tests/routes.test.tsx` as a product decision rather than a test
 * detail — see the note there.
 *
 * ## Its head, for an appeal the build did not know
 *
 * The two seeded appeals are in `data/seo.ts` and get a pre-rendered head and a sitemap entry.
 * One added in Steward since the last build is not, and would otherwise wear no title of its
 * own, so the page writes one from the appeal — through `useSeo`, which runs after the shell's.
 * For a slug the tree does describe it passes no site, and `useSeo` does nothing: the
 * hand-written description there is better than one cut from the card copy.
 */
export function DonationDetailsPage() {
  const t = useT()
  const s = useText()
  const { intlLocale } = useLocale()
  const { slug } = useParams()
  const appeals = useAppeals()

  const causes = appeals.status === 'loaded' ? appeals.causes : []
  const cause = causes.find((c) => c.slug === slug) ?? causes[0]
  /** The other open appeals — see the row at the foot of this page. */
  const others = causes.filter((other) => other.status === 'active' && other.slug !== cause?.slug)

  const described = ROUTES.some((route) => route.path === `/donations/${cause?.slug}`)
  useSeo(cause && !described ? SITE : undefined, {
    path: `/donations/${cause?.slug ?? ''}`,
    name: cause?.title.en ?? '',
    title: `${cause?.title.en ?? ''} — donate`,
    description: cause?.descr.en ?? '',
    image: cause?.imageAlt ? { url: cause.image, alt: cause.imageAlt.en } : undefined,
  })

  if (!cause) {
    return (
      <div className="ul-container ul-section-spacing ul-plain-page-top">
        {/* The page's one `<h1>`, in every state — `audit:seo` counts them. */}
        <h1 className="ul-plain-page-title">{t('donate.heading')}</h1>
        {appeals.status === 'failed' ? (
          <p className="ul-dondir-state" role="alert">
            {t('donate.failed')}
            <button type="button" onClick={appeals.retry}>
              {t('donate.retry')}
            </button>
          </p>
        ) : appeals.status === 'loading' ? (
          <p className="ul-dondir-state" role="status">
            {t('donate.loading')}
          </p>
        ) : (
          <p className="ul-dondir-state">{t('donate.none')}</p>
        )}
      </div>
    )
  }

  return (
    <>
      {/* No banner and no breadcrumb (2026-09-21) — see `ContactPage` for the two things
          that travel with dropping one: the page's `<h1>`, which `Breadcrumb` used to
          carry, and `header.plainRoutes`, which nothing fails without. */}
      <div className="ul-container ul-section-spacing ul-plain-page-top">
        {/* The appeal's own name, and the page's one `<h1>`. The summary panel below
            repeats it as an `<h3>`, which is right — that is the panel's heading, not the
            page's. */}
        <h1 className="ul-plain-page-title">{s(cause.title)}</h1>

        <div className="row gx-0 gy-4 flex-column-reverse flex-lg-row">
          {/* left sidebar */}
          <div className="col-lg-4">
            <InnerSidebar {...INNER_SIDEBAR} />
          </div>

          {/* donation details content */}
          <div className="col-lg-8">
            <div className="ul-donation-details">
              <div className="ul-donation-details-img">
                <img src={cause.image} alt={cause.imageAlt ? s(cause.imageAlt) : ''} />
              </div>

              <h2 className="ul-donation-details-raised">
                {t('donate.raised')}{' '}
                {cause.raised > 0
                  ? formatMoney(cause.raised, cause.currency, intlLocale)
                  : t('donate.raised.unconfirmed')}{' '}
                <span className="target">
                  {t('donate.goal')} {formatMoney(cause.goal, cause.currency, intlLocale)}
                </span>
              </h2>

              <div className="ul-donation-progress ul-donation-progress-2">
                <ProgressBar
                  value={cause.progress}
                  containerClassName="donation-progress-container"
                  barClassName="donation-progressbar"
                  labelClassName="donation-progress-label"
                />
              </div>

              <div className="ul-donation-details-summary">
                <h3 className="ul-donation-details-summary-title">{s(cause.title)}</h3>
                <div className="ul-donation-details-summary-txt">
                  {cause.body.map((paragraph) => (
                    <p key={paragraph.en}>{s(paragraph)}</p>
                  ))}
                </div>
              </div>

              {/* The form, after the appeal — the case for giving before the control for it.
                  A closed appeal takes no gifts, so it gets a sentence and no form. */}
              <div className="ul-dondir-give ul-donation-details-give">
                {cause.status === 'active' ? (
                  <GiveForm appeal={cause.slug} currency={cause.currency} />
                ) : (
                  <p className="ul-dondir-give-note">{t('donate.closed')}</p>
                )}
              </div>

              {/*
                The other appeals.

                Added 2026-09-19 with the directory. Somebody who has read one appeal to the
                end is the likeliest person on the site to give to a different one, and
                until now this page's only way onward was the browser's back button.

                It is also where `DonationCard` lives now. The directory replaced the grid
                on `/donations` that used to be its only consumer, and rather than enter it
                in `reuse-audit.mjs`'s `RETAINED` list beside `Cta` and `Testimonial` — three
                orphaned components is an audit losing its teeth — it has a real one.
              */}
              {others.length > 0 && (
                <section className="ul-donation-details-others">
                  <SectionHeading
                    subTitle={t('donate.eyebrow')}
                    title={t('donate.others')}
                    innerClassName="left"
                  />
                  <div className="row ul-bs-row row-cols-lg-3 row-cols-sm-2 row-cols-1">
                    {others.map((other) => (
                      <div className="col" key={other.slug}>
                        <DonationCard
                          image={other.image}
                          imageAlt={other.imageAlt ? s(other.imageAlt) : undefined}
                          tag={s(other.tag)}
                          title={s(other.title)}
                          descr={s(other.descr)}
                          progress={other.progress}
                          raisedLabel={`${t('donate.raised')} : ${
                            other.raised > 0
                              ? formatMoney(other.raised, other.currency, intlLocale)
                              : t('donate.raised.unconfirmed')
                          }`}
                          goalLabel={`${t('donate.goal')} : ${formatMoney(other.goal, other.currency, intlLocale)}`}
                          to={`/donations/${other.slug}`}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
